import { DatePicker, Radio, Select } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Field, Form, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IDEAL_POSTCODE_API_KEY } from "@/Constants/common";
import { toggleResumeUploaded } from "@/reducers/candidateCreate";
import { fetchFieldData } from "@/reducers/clientcommon";
import { toastMessageText } from "@/utilities/CommonText";
import { showErrorToast } from "@/utilities/utils";

import Button from "../Button";
import FieldErrorMessage from "../common/FieldErrorMessage";
import SelectNoContent from "../common/SelectNoContent";
import Label from "../forms/Label";
import { CalendarIcon } from "../Icons/CalendarIcon";
import { RejectIcon } from "../Icons/RejectIcon";
import { SelectDownIcon } from "../Icons/SelectDownIcon";

dayjs.extend(customParseFormat);

const normalizeGenderValue = (gender) => {
  if (!gender) return "";
  const normalizedGender = String(gender).trim().toLowerCase();
  return ["male", "female", "other"].includes(normalizedGender)
    ? normalizedGender
    : "";
};

const splitCandidateIds = (ids) => {
  if (Array.isArray(ids)) return ids;
  if (ids === null || ids === undefined || ids === "") return [];
  return String(ids)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

const resolveOptionValue = (options = [], value) => {
  const matchedOption = options.find(
    (item) => String(item.id) === String(value),
  );
  return matchedOption ? matchedOption.id : value;
};

const normalizeIdsForOptions = (ids, options = []) =>
  splitCandidateIds(ids).map((id) => resolveOptionValue(options, id));

const filterSelectOptionByName = (input, option) =>
  String(option?.children || "")
    .toLowerCase()
    .includes(input.toLowerCase());

const parseDatePickerValue = (value) => {
  if (!value) return null;
  const supportedFormats = [
    "DD/MM/YYYY",
    "D/M/YYYY",
    "DD-MM-YYYY",
    "D-M-YYYY",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
  ];
  const parsedDate = dayjs(String(value).trim(), supportedFormats, true);
  if (parsedDate.isValid()) return parsedDate;

  const fallbackDate = dayjs(value);
  return fallbackDate.isValid() ? fallbackDate : null;
};

const CandidatePersonalForm = ({ fromEdit = false, candidateData = {} }) => {
  const dispatch = useDispatch();
  const {
    values,
    submitForm,
    setValues,
    errors,
    touched,
    submitCount,
    isSubmitting,
    setFieldValue,
  } = useFormikContext();
  const { isResumeUploaded } = useSelector((state) => state.candidateCreate);
  const candidateDropdownData =
    useSelector((state) => state.clientCommon.candidateDropdownData) || {};
  const {
    title_list = [],
    band_list = [],
    consultants_list = [],
    counties_list = [],
    division_list = [],
    // status_list = [],
    regions_list = [],
    referrals_list = [],
  } = candidateDropdownData;

  const handleRoleChange = (roleId, settingId = values.setting) => {
    const callback = (val) => {
      setSkillsList(val);
      if (fromEdit && candidateData?.candidate_skill_ids) {
        let selectedSkills = normalizeIdsForOptions(
          candidateData?.candidate_skill_ids,
          val,
        );
        setValues((prev) => ({
          ...prev,
          skills: selectedSkills,
          hasSkills: val && val.length > 0,
        }));
      } else {
        setFieldValue("hasSkills", val && val.length > 0);
      }
    };
    setValues((prev) => ({
      ...prev,
      role: roleId,
    }));
    dispatch(
      fetchFieldData({
        id: roleId,
        setting_id: settingId,
        field_type: "filtered_speciality",
        callback,
      }),
    );
  };
  const handleSettingsChange = (settingId, additionalValues) => {
    const callback = (val) => {
      setRolesList(val);
      if (fromEdit && candidateData?.candidate_type_id) {
        let selectedRoleId = val?.find(
          (item) =>
            String(item.id) === String(candidateData?.candidate_type_id),
        )
          ? resolveOptionValue(val, candidateData?.candidate_type_id)
          : null;
        if (selectedRoleId) {
          handleRoleChange(selectedRoleId, settingId);
        }
      }
    };
    setValues((prev) => ({
      ...prev,
      ...additionalValues,
      setting: settingId,
      role: null,
      skills: [],
      hasSkills: false,
    }));
    dispatch(
      fetchFieldData({
        setting_id: settingId,
        field_type: "role",
        callback,
      }),
    );
  };
  useEffect(() => {
    let additionalValues = {};
    if (!fromEdit && candidateDropdownData?.title_list?.length > 0) {
      // setValues((prev) => ({
      //   ...prev,
      //   title: candidateDropdownData.title_list[0].id,
      // }))
      additionalValues.title = candidateDropdownData.title_list[0].id;
    }
    if (candidateDropdownData?.division_list?.length > 0) {
      const defaultSettingId = fromEdit
        ? candidateData?.candidate_parent_id
          ? resolveOptionValue(
              candidateDropdownData.division_list,
              candidateData?.candidate_parent_id,
            )
          : candidateDropdownData.division_list[0].id
        : candidateDropdownData.division_list[0].id;
      handleSettingsChange(defaultSettingId, additionalValues);
    } else {
      setValues((prev) => ({
        ...prev,
        ...additionalValues,
      }));
    }
  }, [
    candidateDropdownData,
    candidateData?.candidate_parent_id,
    candidateData?.candidate_type_id,
    candidateData?.candidate_skill_ids,
    fromEdit,
  ]);
  const [addLoading, setAddLoading] = useState(false);
  const [addList, setAddList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [skillsList, setSkillsList] = useState([]);

  const [clientCoords, setClientCoords] = useState({
    lat: values?.clientLat || "",
    lng: values?.clientLng || "",
  });

  /**
   * Fetches address data based on the client's postcode, processes the response, and updates the UI accordingly.
   */
  const findAddress = async (postcode, callback) => {
    console.log(postcode);
    setAddLoading(true);

    // const url = `https://api.getAddress.io/v2/uk/${postcode}?api-key=${__FIND_LOCATION_API_KEY}`;
    const url = `https://api.ideal-postcodes.co.uk/v1/postcodes/${postcode}?api_key=${IDEAL_POSTCODE_API_KEY}`;

    try {
      const _response = await fetch(url);
      const response = await _response.json();
      const data = response.result || [];
      console.log(data);
      // const data = [];
      console.log("Address fetch response:", data);

      let __addList = [];

      if (data.length === 0) {
        showErrorToast(toastMessageText.noAddressFoundForPostCode);
        setAddList([]);
        setAddLoading(false);
        return;
      }
      data.forEach((item) => {
        let address = `${item.line_1}, ${item.line_2}, ${item.line_3}, ${item.post_town}, ${item.county}, ${item.postcode}`;

        __addList.push(
          address
            .split(", ")
            .filter((i) => i !== "")
            .join(", "),
        );
      });

      setAddList(__addList);
      setAddLoading(false);
      callback && callback();
    } catch (error) {
      console.log(error);
      console.error("Error fetching address:", error);
      const message =
        error?.Message ||
        error?.message ||
        "Unable to connect. Please try again.";
      if (message.includes("Invalid postcode")) {
        showErrorToast(toastMessageText.ukPostCodeOnly);
      } else {
        showErrorToast(toastMessageText.unableToConnect);
      }
      setAddList([]);
      setAddLoading(false);
    }
  };

  const findCounty = async () => {
    let response = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(values.postCode)}`,
      {
        method: "GET",
      },
    );
    try {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let county = data.result.pfa?.toLowerCase();
      let selectedCountyFromList = counties_list.find(
        (item) => item.name.toLowerCase() === county,
      );
      let regionFromCountyId = regions_list.find(
        (item) => item.id === selectedCountyFromList?.region_id,
      );
      setValues((prev) => ({
        ...prev,
        county: selectedCountyFromList?.id,
        region: regionFromCountyId?.id,
        candidate_lat: clientCoords.lat,
        candidate_lng: clientCoords.lng,
      }));
    } catch (error) {
      showErrorToast("Unable to fetch county from postcode");
      setValues((prev) => ({
        ...prev,
        county: null,
        candidate_lat: clientCoords.lat,
        candidate_lng: clientCoords.lng,
      }));
    }
  };

  useEffect(() => {
    if (values.postCode && isResumeUploaded) {
      findAddress(values.postCode, () => {
        setFieldValue("selectedAddress", "");
      });
      findCounty();
      dispatch(toggleResumeUploaded(false));
    }
  }, [values.postCode]);

  useEffect(() => {
    const normalizedGender = normalizeGenderValue(values.gender);
    if (
      values.gender &&
      normalizedGender &&
      normalizedGender !== values.gender
    ) {
      setFieldValue("gender", normalizedGender, false);
    }
  }, [values.gender, setFieldValue]);

  const hasFieldError = (fieldName) =>
    Boolean(errors[fieldName] && (submitCount > 0 || touched[fieldName]));
  const fieldErrorClass = (fieldName) =>
    hasFieldError(fieldName) ? "!border-red-500" : "";
  const fieldErrorStatus = (fieldName) =>
    hasFieldError(fieldName) ? "error" : undefined;

  return (
    <Form>
      <div className="grid gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="title" isRequired={true}>
              Title
            </Label>
            <Select
              status={fieldErrorStatus("title")}
              name="title"
              placeholder="Select Title"
              suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
              value={values.title}
              onChange={(val) => {
                setValues((prev) => ({
                  ...prev,
                  title: val,
                }));
              }}
            >
              {title_list.map((item, index) => {
                const { id, name } = item;
                return (
                  <Select.Option value={id} key={index}>
                    {name}
                  </Select.Option>
                );
              })}
            </Select>
            <FieldErrorMessage name="title" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="firstName" isRequired={true}>
              First Name
            </Label>
            <Field
              className={fieldErrorClass("firstName")}
              name="firstName"
              type="text"
              placeholder="First Name"
            />
            <FieldErrorMessage name="firstName" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="lastName" isRequired={true}>
              Last Name
            </Label>
            <Field
              className={fieldErrorClass("lastName")}
              name="lastName"
              type="text"
              placeholder="Last Name"
            />
            <FieldErrorMessage name="lastName" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="gender" isRequired={true}>
              Gender
            </Label>
            <Radio.Group
              className={
                hasFieldError("gender")
                  ? "[&_.ant-radio-inner]:!border-red-500"
                  : ""
              }
              name="gender"
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  gender: e.target.value,
                }));
              }}
              value={normalizeGenderValue(values.gender)}
            >
              <Radio value="male">Male</Radio>
              <Radio value="female">Female</Radio>
              <Radio value="other">Other</Radio>
            </Radio.Group>
            <FieldErrorMessage name="gender" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="email" isRequired={true}>
              Email
            </Label>
            <Field
              className={fieldErrorClass("email")}
              name="email"
              type="email"
              autoComplete="off"
              placeholder="Email"
            />
            <FieldErrorMessage name="email" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="mobile" isRequired={true}>
              Mobile
            </Label>
            <Field
              className={fieldErrorClass("mobile")}
              name="mobile"
              type="text"
              placeholder="Mobile"
            />
            <FieldErrorMessage name="mobile" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="dateOfBirth" isRequired={true}>
              Date of Birth
            </Label>
            <div className="customDatePicker">
              <DatePicker
                status={fieldErrorStatus("dateOfBirth")}
                id="dateOfBirth"
                format={{
                  format: "DD-MM-YYYY",
                  type: "mask",
                }}
                value={
                  values.dateOfBirth
                    ? parseDatePickerValue(values.dateOfBirth)
                    : null
                }
                suffixIcon={
                  <CalendarIcon cls="w-[24px] h-[24px] text-txtColor" />
                }
                onChange={(date) => {
                  setValues((prev) => ({
                    ...prev,
                    dateOfBirth: date ? date.format("DD/MM/YYYY") : "",
                  }));
                  //
                }}
              />
              <FieldErrorMessage name="dateOfBirth" />
            </div>
          </div>
          {division_list.length > 0 && (
            <div className="grid gap-3 self-baseline">
              <Label htmlFor="setting" isRequired={true}>
                Setting
              </Label>

              <Select
                status={fieldErrorStatus("setting")}
                name="setting"
                placeholder="Select Setting"
                suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                onChange={(val) => {
                  handleSettingsChange(val);
                }}
                value={values.setting}
              >
                {division_list.map((item, index) => {
                  return (
                    <Select.Option value={item.id} key={index}>
                      {item.name}
                    </Select.Option>
                  );
                })}
              </Select>
              <FieldErrorMessage name="setting" />
            </div>
          )}
          {rolesList.length > 0 && (
            <div className="grid gap-3 self-baseline">
              <Label htmlFor="role" isRequired={true}>
                Role
              </Label>
              <Select
                status={fieldErrorStatus("role")}
                name="role"
                showSearch
                optionFilterProp="children"
                filterOption={filterSelectOptionByName}
                placeholder="Select Role"
                suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                value={values.role}
                onChange={(val) => {
                  handleRoleChange(val);
                }}
              >
                {rolesList.map((item, index) => {
                  return (
                    <Select.Option value={item.id} key={index}>
                      {item.name}
                    </Select.Option>
                  );
                })}
              </Select>
              <FieldErrorMessage name="role" />
            </div>
          )}

          {skillsList.length > 0 && (
            <div className="grid gap-3 self-baseline">
              <Label htmlFor="skills" isRequired={true}>
                Skills
              </Label>
              <Select
                status={fieldErrorStatus("skills")}
                name="skills"
                showSearch
                optionFilterProp="children"
                filterOption={filterSelectOptionByName}
                placeholder="Select Skills"
                suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                mode="multiple"
                value={values.skills}
                onChange={(val) => {
                  setValues((prev) => ({
                    ...prev,
                    skills: val,
                  }));
                }}
              >
                {skillsList.map((item, index) => {
                  return (
                    <Select.Option value={item.id} key={index}>
                      {item.name}
                    </Select.Option>
                  );
                })}
              </Select>
              <FieldErrorMessage name="skills" />
            </div>
          )}
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="gmcNumber" isRequired={true}>
              GMC Number
            </Label>
            <div className="flex flex-col gap-1">
              <Field
                className={fieldErrorClass("gmcNumber")}
                name="gmcNumber"
                type="text"
                placeholder="GMC Number"
              />
              <FieldErrorMessage name="gmcNumber" />
            </div>
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="postCode" isRequired={true}>
              Post Code
            </Label>
            <div className="relative">
              <Field
                className={fieldErrorClass("postCode")}
                type="text"
                autoComplete="off"
                name="postCode"
                placeholder="Enter Post Code"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[14px] font-medium text-primary outline-none !ring-0"
                onClick={() => {
                  if (
                    values?.postCode === "" ||
                    values?.postCode === null ||
                    values?.postCode === undefined
                  ) {
                    showErrorToast(toastMessageText.postCodeRequired);
                    return;
                  }
                  if (addLoading) return;
                  setAddLoading(true);
                  findAddress(values.postCode, () => {
                    setFieldValue("selectedAddress", "");
                  });
                  findCounty();
                }}
              >
                {addLoading ? "Loading..." : "Find Address"}
              </button>
            </div>
            <FieldErrorMessage name="postCode" />
          </div>

          {addList.length > 0 ? (
            <div className="grid gap-3 self-baseline">
              <Label
                htmlFor="address"
                // className="text-[14px] font-medium leading-[normal] text-black"
              >
                Select Address
              </Label>
              <Select
                status={fieldErrorStatus("address")}
                showSearch
                style={{ width: "100%" }}
                placeholder={"Select Address"}
                optionFilterProp="children"
                suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                onChange={(value) => {
                  setValues((prev) => ({
                    ...prev,
                    address: value,
                  }));
                }}
                allowClear={{
                  clearIcon: <RejectIcon cls="w-[20px] h-[20px] text-red" />,
                }}
                notFoundContent={<SelectNoContent />}
              >
                {addList.map((item, index) => {
                  return (
                    <Select.Option value={item} key={index}>
                      {item}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          ) : null}
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="address" isRequired={true}>
              Address
            </Label>

            <div className="flex flex-col gap-1">
              <Field
                type="text"
                className={fieldErrorClass("address")}
                autoComplete="off"
                name="address"
                value={values.address}
                placeholder="Enter Address"
                onChange={(e) => {
                  setFieldValue("address", e.target.value);
                }}
                // validate={(value) => {
                //   if (!value || value.trim() === "") {
                //     return "Address is required";
                //   }
                // }}
              />
              <FieldErrorMessage name="address" />
            </div>
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="county">County</Label>
            <div className="flex flex-col gap-1">
              <Select
                status={fieldErrorStatus("county")}
                showSearch
                style={{ width: "100%" }}
                placeholder={"Select"}
                optionFilterProp="children"
                className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                // allowClear={{
                //   clearIcon: (
                //     <RejectIcon cls="w-[20px] h-[20px] text-red" />
                //   ),
                // }}
                value={values.county == "" ? null : values.county}
                notFoundContent={<SelectNoContent />}
              >
                {counties_list?.map((item, index) => {
                  const { id, name } = item;
                  return (
                    <Select.Option value={id} key={index}>
                      {name}
                    </Select.Option>
                  );
                })}
              </Select>
              <FieldErrorMessage name="county" />
            </div>
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="region">Region</Label>
            <div className="flex flex-col gap-1">
              <Select
                status={fieldErrorStatus("region")}
                showSearch
                style={{ width: "100%" }}
                placeholder={"Select"}
                optionFilterProp="children"
                // className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                // allowClear={{
                //   clearIcon: (
                //     <RejectIcon cls="w-[20px] h-[20px] text-red" />
                //   ),
                // }}
                value={values.region}
                notFoundContent={<SelectNoContent />}
              >
                {regions_list.map((item, index) => {
                  const { id, name } = item;
                  return (
                    <Select.Option value={id} key={index}>
                      {name}
                    </Select.Option>
                  );
                })}
              </Select>
              <FieldErrorMessage name="region" />
            </div>
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="band" isRequired={true}>
              Band
            </Label>
            <Select
              status={fieldErrorStatus("band")}
              name="band"
              placeholder="Select Band"
              // className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
              suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
              value={values.band}
              onChange={(val) => {
                setValues((prev) => ({
                  ...prev,
                  band: val,
                }));
              }}
              notFoundContent={<SelectNoContent />}
            >
              {band_list.map((item, index) => {
                return (
                  <Select.Option value={item.id} key={index}>
                    {item.name}
                  </Select.Option>
                );
              })}
            </Select>
            <FieldErrorMessage name="band" />
          </div>
          <div className="grid gap-3 self-baseline">
            <Label htmlFor="referral" isRequired={true}>
              Referral Source
            </Label>
            <Select
              status={fieldErrorStatus("referral")}
              name="referral"
              placeholder="Select Referral"
              // className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
              suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
              value={values.referral}
              notFoundContent={<SelectNoContent />}
              onChange={(val) => {
                setValues((prev) => ({
                  ...prev,
                  referral: val,
                }));
              }}
            >
              {referrals_list.map((item, index) => {
                if (item.name.toLowerCase().includes("existing")) {
                  return null;
                }
                return (
                  <Select.Option value={item.id} key={index}>
                    {item.name}
                  </Select.Option>
                );
              })}
            </Select>
            <FieldErrorMessage name="referral" />
          </div>
        </div>
        <div>
          <Button
            type="button"
            className={`transition-linear relative h-[40px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary ${isSubmitting ? "cursor-wait" : "cursor-pointer"}`}
            onClick={() => {
              if (isSubmitting) return;
              submitForm();
            }}
            disabled={isSubmitting}
          >
            {fromEdit ? "Next" : "Submit"}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default CandidatePersonalForm;
